import React, { useEffect, useRef, useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import Alert from 'react-bootstrap/Alert'
import Spinner from 'react-bootstrap/Spinner'
import { api } from '../shared/ApiProvider'

function DeleteSurveyModal({
                             setShowDeleteSurveyModal,
                             showDeleteSurveyModal,
                             responseId,
                             onDeleteSuccess,
                           }) {
  const [error, setError] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const isMountedRef = useRef(true)
  const abortControllerRef = useRef(null)
  const timeoutIdRef = useRef(null)

  const deleteSurvey = async () => {
    if (!responseId) return

    // Cancel previous request if exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    abortControllerRef.current = new AbortController()
    timeoutIdRef.current = setTimeout(() => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }, 30000)

    setIsDeleting(true)
    setError(null)

    try {
      const url = `/archive/${responseId}`
      const response = await api.put(url, null, {
        signal: abortControllerRef.current.signal,
      })

      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current)
        timeoutIdRef.current = null
      }

      if (!isMountedRef.current) return

      // Axios only reaches here for 2xx responses
      onDeleteSuccess?.(responseId)
      setShowDeleteSurveyModal(false)
      setError(null)


    } catch (err) {
      if (!isMountedRef.current) return

      clearTimeout(timeoutIdRef.current)
      timeoutIdRef.current = null
      if (err.name === 'AbortError') {
        setError('Request timed out. Please try again.')
        return
      }
      // Axios error structure
      const status = err.response?.status
      const message = err.response?.data?.message
      if (status === 404) {
        setError('Survey response not found. It may have already been deleted.')
      } else if (status === 403) {
        setError('You do not have permission to delete this survey response.')
      } else if (status >= 500) {
        setError('Server error. Please try again later.')
      } else {
        setError(message || 'Failed to delete survey response. Please try again.')
      }
      console.error('Delete error:', err)
    } finally {
      if (isMountedRef.current) {
        setIsDeleting(false)
      }
    }
  }

  const closeModal = () => {
    if (!isDeleting) {
      setShowDeleteSurveyModal(false)
      setError(null)
    }
  }

  useEffect(() => {
    return () => {
      isMountedRef.current = false
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current)
      }
    }
  }, [])

  return (
    <Modal
      show={showDeleteSurveyModal}
      onHide={closeModal}
      backdrop={isDeleting ? 'static' : true}
      keyboard={!isDeleting}
      aria-labelledby="delete-modal-title"
      aria-describedby="delete-modal-description"
    >
      <Modal.Header closeButton={!isDeleting}>
        <Modal.Title id="delete-modal-title">
          Delete Survey Response
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => setError(null)}
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
          >
            {error}
          </Alert>
        )}
        <p id="delete-modal-description">
          Are you sure you want to delete this survey response? This action
          cannot be undone.
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={closeModal}
          disabled={isDeleting}
          aria-label="Cancel deletion"
        >
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={deleteSurvey}
          disabled={isDeleting}
          aria-busy={isDeleting}
          aria-label="Confirm deletion"
        >
          {isDeleting ? (
            <>
              <Spinner
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              <span aria-live="polite">Deleting...</span>
            </>
          ) : (
            'Delete'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default DeleteSurveyModal
